import { formatLocalDateTime } from '../../utils/localDateTime';
import React from 'react';
import { reportAreaLabel, type DailyAreaReport } from '../../../functions/src/common/dailyReportArea';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import { dailyMicroEventMarker } from '../../utils/dailyMicroEvents';

export default function DailyAreaReportDisplay({ result }: { result: DailyAreaReport }) {
    const { isLoaded } = useGoogleMaps();
    const stale = Date.now() - Date.parse(result.generatedAt) > 86_400_000;
    const markers = result.localEvents
        .flatMap((event, index) => {
            const marker = dailyMicroEventMarker(event, index);
            return marker ? [marker] : [];
        });
    return (
        <div className="space-y-6 rounded-xl bg-white p-4 text-slate-800 [color-scheme:light]">
            <header className="space-y-2">
                <h2 className="text-xl font-semibold">{reportAreaLabel(result.reportArea)}</h2>
                <p className="text-sm">{formatLocalDateTime(result.windowStart)} – {formatLocalDateTime(result.generatedAt)}</p>
                <p>{result.summary}</p>
                {stale && <p role="status" className="text-amber-700">This report is over 24 hours old. Reopen Daily Micro from the dashboard to refresh.</p>}
            </header>
            <section aria-label="Report-day event map" className="space-y-3">
                <h3 className="text-lg font-semibold">Report-day event map</h3>
                <div className="h-[480px] overflow-hidden rounded-xl border border-slate-200">
                    <UnifiedMap
                        isLoaded={isLoaded}
                        initialViewState={markers.length ? { longitude: markers[0].lng, latitude: markers[0].lat, zoom: 5 } : undefined}
                        markers={markers}
                        alerts={[]}
                        filterIso3={result.reportArea.country}
                        highlightIso3={result.reportArea.country}
                        choroplethEnabled
                        showGlobalAssets={false}
                        theme="light"
                        interactive={true}
                        hideSidebar={true}
                    />
                </div>
            </section>
            <section aria-label="Local events" className="space-y-3">
                <h3 className="text-lg font-semibold">Local events</h3>
                {!result.localEvents.length && <p role="status">No new events have been recorded within the last 24 hours.</p>}
                {result.localEvents.map(event => <article key={event.id} className="rounded-xl border border-slate-200 p-4">
                    <h4 className="font-semibold">{event.title}</h4>
                    <p className="mt-1 text-sm text-slate-500">{event.location} · {formatLocalDateTime(event.publishedAt)}</p>
                    <p className="mt-2 whitespace-pre-wrap">{event.summary}</p>
                </article>)}
            </section>
            <aside className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
                <h3 className="font-semibold">Coverage limits</h3>
                <p>{result.coverage.reviewedCount} dated, approved country events reviewed. {result.coverage.unknownLocationCount} lacked a reliable state. {result.coverage.unknownTimeCount} lacked a usable publication time.</p>
                <p className="mt-2">Only events with explicit state locations are included. City-only reports, incomplete feeds, and missing locations may leave gaps.</p>
                {result.coverage.omittedEventCount > 0 && <p className="mt-2">{result.coverage.omittedEventCount} older matching events are omitted. The latest 100 are shown.</p>}
            </aside>
            <section className="space-y-2 border-t border-slate-200 pt-4">
                <h3 className="text-lg font-semibold">National context · {result.country}</h3>
                <p className="text-sm text-slate-500">Country-wide background; not a state risk assessment. {result.nationalContext.updatedAt ? `Updated ${formatLocalDateTime(result.nationalContext.updatedAt)}.` : 'Update time unavailable.'}</p>
                <p className="whitespace-pre-wrap">{result.nationalContext.summary}</p>
            </section>
        </div>
    );
}
